import React, { useEffect, useRef, useState } from 'react'
import { Alert, MenuItem, Stack, Table, TableBody, TableCell, TableHead, TableRow, TextField, Typography } from '@mui/material'
import { SmartDialog } from '../../generic/utilities/SmartDialog'
import { apiGet } from '../../generic/Api_Functions'
import { commitBomImport, discardBomImport, readBomLibraryError } from './bomLibraryApi'
import { translateBomLibraryError } from './bomLibraryErrors'
import { useSmartTranslation } from '../../generic/hooks/useSmartTranslation'
import useBomLibraryIndexSetting, { DEFAULT_INDEX_VALUE_WITHOUT_INDEX } from './useBomLibraryIndexSetting'

/**
 * BomImportDialog — the COMMIT step of a library import (S28, historia 117 HU-02). The file has
 * already been uploaded by the library list's table import button into `importScopeUUID` (an import
 * scope generated by the client, distinct from the editor's `tempWorkflowUUID` — contrato-sun.md §7
 * desviación 20); nothing is persisted until the user commits here with code, name and index (N13).
 * Closing without committing discards the scope (N14).
 *
 * (The BOM detail's "New version (import)" no longer uses this dialog: it commits automatically
 * after the upload, see `BomLibraryDetail.jsx`.)
 *
 * @param {object} props
 * @param {string} props.importScopeUUID scope already holding the uploaded file
 * @param {function()} props.onClose
 * @param {function(object)} props.onCommitted called with the `BomVersionSaveResult`
 */
const BomImportDialog = ({ onClose, onCommitted, importScopeUUID }) => {
  const { t_ } = useSmartTranslation()
  const [code, setCode] = useState('')
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [indexValue, setIndexValue] = useState('')
  const [indexOptions, setIndexOptions] = useState([])
  const [duplicateResolution, setDuplicateResolution] = useState('')
  const [error, setError] = useState('')
  const [invalidLines, setInvalidLines] = useState([])
  const [duplicateGroups, setDuplicateGroups] = useState([])
  const committedRef = useRef(false)

  // PO adjustment 2026-09-10 (RN-G20): with USE_WORKFLOW_INDEX off no index is asked for here —
  // the catalogue is not requested and the committed version is born APPROVED.
  const { useIndex, loaded: indexSettingLoaded } = useBomLibraryIndexSetting()

  useEffect(() => {
    // Gated by `indexSettingLoaded`: `useIndex` starts as `true` while the setting is in flight.
    if (!indexSettingLoaded || !useIndex) return
    apiGet('indexValues/available').then((options) => setIndexOptions(options || []))
  }, [indexSettingLoaded, useIndex])

  const handleClose = async () => {
    if (!committedRef.current) {
      await discardBomImport(importScopeUUID)
    }
    onClose()
  }

  const handleCommit = async () => {
    setError('')
    setInvalidLines([])
    setDuplicateGroups([])
    try {
      const result = await commitBomImport(importScopeUUID, {
        targetBomId: null,
        code,
        name,
        description: description || '',
        indexValue: useIndex ? indexValue : DEFAULT_INDEX_VALUE_WITHOUT_INDEX,
        duplicateResolution: duplicateResolution || null
      })
      committedRef.current = true
      onCommitted(result)
    } catch (thrown) {
      const { error: literal, context } = readBomLibraryError(thrown)
      setError(literal || t_('Not allowed'))
      if (context.invalidLines) setInvalidLines(context.invalidLines)
      if (context.duplicateGroups) setDuplicateGroups(context.duplicateGroups)
    }
  }

  return (
    <SmartDialog
      setOpen
      title={{ render: <Typography variant='h6'>{t_('Import BOM')}</Typography> }}
      acceptLabel='Commit'
      acceptCallback={handleCommit}
      cancel={false}
      close
      closeCallback={handleClose}
      autoClose={false}
      renderComponent={
        <Stack spacing={2} sx={{ mt: 1, minWidth: 320, textAlign: 'left' }} data-testid='bom-import-dialog'>
          {error && <Alert severity='error'>{translateBomLibraryError(t_, error)}</Alert>}
          {invalidLines.length > 0 && (
            <Table size='small'>
              <TableHead><TableRow><TableCell>{t_('Line')}</TableCell><TableCell>{t_('Reason')}</TableCell></TableRow></TableHead>
              <TableBody>
                {invalidLines.map((invalidLine) => (
                  <TableRow key={invalidLine.line}><TableCell>{invalidLine.line}</TableCell><TableCell>{translateBomLibraryError(t_, invalidLine.reason)}</TableCell></TableRow>
                ))}
              </TableBody>
            </Table>
          )}
          {duplicateGroups.length > 0 && (
            <TextField
              select
              label={t_('Duplicate resolution')}
              value={duplicateResolution}
              onChange={(event) => setDuplicateResolution(event.target.value)}
            >
              <MenuItem value='SUM'>{t_('Sum quantities')}</MenuItem>
              <MenuItem value='REJECT'>{t_('Reject')}</MenuItem>
            </TextField>
          )}
          <TextField label={t_('Code')} value={code} onChange={(event) => setCode(event.target.value)} />
          <TextField label={t_('Name')} value={name} onChange={(event) => setName(event.target.value)} />
          <TextField label={t_('Description')} value={description} onChange={(event) => setDescription(event.target.value)} />
          {useIndex && (
            <TextField
              select
              label={t_('Index')}
              value={indexValue}
              onChange={(event) => setIndexValue(event.target.value)}
            >
              {indexOptions.map((option) => (
                <MenuItem key={option.id} value={option.name}>{option.name}</MenuItem>
              ))}
            </TextField>
          )}
        </Stack>
      }
    />
  )
}

export default BomImportDialog
